import { useRef, useEffect, useCallback } from 'react';
import { debugLog, debugError } from '../utils/debug';

export const useAudio = () => {
    const audioContextRef = useRef(null);

    const initializeAudioContext = useCallback(() => {
        if (!audioContextRef.current && (window.AudioContext || window.webkitAudioContext)) {
            try {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                debugLog('useAudio', 'initializeAudioContext', 'AudioContext initialized successfully');
            } catch (e) {
                debugError('useAudio', 'initializeAudioContext', 'Error initializing AudioContext:', e);
                console.error('Impossibile inizializzare l\'audio:', e);
            }
        } else if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume().then(() => {
                debugLog('useAudio', 'initializeAudioContext', 'AudioContext resumed successfully');
            }).catch(e => {
                debugError('useAudio', 'initializeAudioContext', 'Error resuming AudioContext:', e);
            });
        }
        return audioContextRef.current;
    }, []);

    const cleanupAudioContext = useCallback(() => {
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            try {
                debugLog('useAudio', 'cleanupAudioContext', 'Cleaning up AudioContext');
                if (audioContextRef.current.state === 'running') {
                    audioContextRef.current.suspend().catch(e => {
                        debugError('useAudio', 'cleanupAudioContext', 'Error suspending AudioContext:', e);
                    });
                }
            } catch (e) {
                debugError('useAudio', 'cleanupAudioContext', 'Error during AudioContext cleanup:', e);
            }
        }
    }, []);

    const createReverbImpulse = useCallback((ctx) => {
        if (!ctx) return null;
        const sampleRate = ctx.sampleRate;
        const length = sampleRate * 0.8; // 800ms di riverbero
        const impulse = ctx.createBuffer(2, length, sampleRate);

        for (let channel = 0; channel < impulse.numberOfChannels; channel++) {
            const channelData = impulse.getChannelData(channel);
            for (let i = 0; i < length; i++) {
                channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2.5);
            }
        }
        return impulse;
    }, []);

    const playBeep = useCallback((timeLeft, phase = 'work') => {
        const ctx = initializeAudioContext();
        if (!ctx) return;

        try {
            // Configurazione suono in base alla fase
            let baseFrequency, oscillatorType, duration, beepPattern;
            
            if (phase === 'work') {
                // Suono acuto ed energico per fine lavoro
                baseFrequency = 800; // Frequenza base più alta
                oscillatorType = 'square'; // Onda quadra più "dura"
                duration = 0.2; // Durata leggermente più lunga
                beepPattern = 'double'; // Doppio beep per maggiore enfasi
            } else if (phase === 'rest') {
                // Suono più grave e rilassante per fine riposo
                baseFrequency = 400; // Frequenza base più bassa
                oscillatorType = 'sine'; // Onda sinusoidale più dolce
                duration = 0.15; // Durata più breve
                beepPattern = 'single'; // Singolo beep più soft
            } else {
                // Default (countdown o altri)
                baseFrequency = 600;
                oscillatorType = 'sine';
                duration = 0.15;
                beepPattern = 'single';
            }

            // Calcola la frequenza in base al tempo rimanente
            const frequencyVariation = (5 - timeLeft) * (phase === 'work' ? 300 : 150);
            const frequency = baseFrequency + frequencyVariation;

            const playSingleBeep = (delay = 0) => {
                const oscillator = ctx.createOscillator();
                const gainNode = ctx.createGain();
                const convolver = ctx.createConvolver();
                const reverbGain = ctx.createGain();

                const impulseBuffer = createReverbImpulse(ctx);
                if (!impulseBuffer) return;
                
                convolver.buffer = impulseBuffer;
                reverbGain.gain.value = phase === 'work' ? 0.3 : 0.2; // Meno riverbero per rest

                oscillator.connect(gainNode);
                gainNode.connect(ctx.destination);
                gainNode.connect(convolver);
                convolver.connect(reverbGain);
                reverbGain.connect(ctx.destination);

                oscillator.type = oscillatorType;
                oscillator.frequency.value = frequency;

                const startTime = ctx.currentTime + delay;
                gainNode.gain.setValueAtTime(0, startTime);
                gainNode.gain.linearRampToValueAtTime(1, startTime + 0.01);
                gainNode.gain.linearRampToValueAtTime(1, startTime + duration * 0.6);
                gainNode.gain.linearRampToValueAtTime(0, startTime + duration);

                oscillator.start(startTime);
                oscillator.stop(startTime + duration);
            };

            // Esegui il pattern di beep
            playSingleBeep(0);
            if (beepPattern === 'double') {
                playSingleBeep(0.25); // Secondo beep dopo 250ms
            }
        } catch (e) {
            debugError('useAudio', 'playBeep', `Error creating beep: ${e.message}`);
        }
    }, [createReverbImpulse, initializeAudioContext]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
             // Optional: decide if we want to cleanup on unmount or keep it persistent?
             // The original code cleaned up on timer stop/completion.
        };
    }, []);

    return {
        initializeAudioContext,
        cleanupAudioContext,
        playBeep
    };
};
